import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
} from '@clouway/ui'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { addDigitalPort, removeDigitalPort, updateDigitalPort } from '../configEdits'
import type { DigitalPortConfig } from '../types'
import { DigitalInputEditForm, type DigitalInputFormValues } from '../components/DigitalInputEditForm'
import { useConfigContext } from './ConfigLayout'

const ALL_PORTS = ['0', '1', '2', '3']

export function DigitalInputsConfig() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { config, save, loading } = useConfigContext()
  // null = closed; { original } present = editing that port; {} = adding.
  const [editing, setEditing] = useState<{ original?: number } | null>(null)

  if (loading) return <Skeleton className="h-40 w-full" />

  const ports = config.digitalPortConfigs
  const editingEntry =
    editing?.original !== undefined ? ports.find((p) => p.port === editing.original) : undefined
  const used = new Set(ports.map((p) => String(p.port)))
  const availablePorts = ALL_PORTS.filter((p) => !used.has(p) || String(editingEntry?.port) === p)

  const handleSubmit = (values: DigitalInputFormValues) => {
    const item: DigitalPortConfig = {
      port: Number(values.port),
      type: values.type,
      working: values.working,
      inverse: values.inverse,
    }
    const next =
      editing?.original !== undefined ? updateDigitalPort(config, editing.original, item) : addDigitalPort(config, item)
    return save(next).then(() => setEditing(null))
  }

  return (
    <div className="flex flex-col gap-3">
      {ports.length === 0 && !editing && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('trackingObjects.config.emptyDigitals')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {ports.map((port) => (
        <Card key={port.port}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {t(`trackingObjects.config.digitalInputTypes.${port.type}` as 'trackingObjects.config.digitalInputTypes.door')}{' '}
                · {tCommon('trackingObjects.configs.digitals')} {port.port}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" aria-label="edit" onClick={() => setEditing({ original: port.port })}>
                  <Pencil className="size-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" aria-label="delete">
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('trackingObjects.config.deleteDigitalPort')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('trackingObjects.config.deletePortWarning')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => save(removeDigitalPort(config, port.port))}>
                        {tCommon('ui.button.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}

      {editing ? (
        <DigitalInputEditForm
          availablePorts={availablePorts}
          initialValues={
            editingEntry
              ? {
                  port: String(editingEntry.port),
                  type: editingEntry.type,
                  working: editingEntry.working,
                  inverse: editingEntry.inverse,
                }
              : undefined
          }
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <Button variant="outline" onClick={() => setEditing({})}>
          <Plus className="size-4" />
          {tCommon('trackingObjects.configs.newDigitalPort')}
        </Button>
      )}
    </div>
  )
}
